<template>
  <div class="first-quiz-container">
    <div class="d-flex justify-content-center title mb-4">
      Welcome to EssayGrader - Trusted by 100,000+ teachers 🎉
    </div>
    <div class="d-flex justify-content-center mb-4">
      <h5>{{ quizeSteps[activeStepIndex].title }}</h5>
    </div>
    <eg-stepper
      v-model="loading"
      finish-label="Finish"
      :border-bottom="false"
      @beforeChange="beforeChange"
      @change="activeStepIndex = $event"
      @finish="finishHandler"
    >
      <template
        v-for="(step, index) in quizeSteps"
        :slot="'slot-' + (index + 1)"
      >
        <component
          :is="step.component || 'quize-step'"
          :key="index"
          v-bind="step.props"
          v-model="step.answer"
          :other-value="step.otherAnswer"
          :options="step.options"
          :placeholder="step.placeholder"
          :is-multiselect="step.isMultiselect"
          @input:other="handleInputOther(index, $event)"
        />
      </template>

      <template
        #actions="{
          currentStepIndex,
          steps,
          loading: slotLoading,
          goPrev,
          goNext,
        }"
      >
        <div class="stepper-actions d-flex justify-content-between mt-5">
          <eg-b-button
            :disabled="currentStepIndex === 0"
            variant="link"
            class="text-info"
            @click="goPrev"
          >
            <div v-if="currentStepIndex !== 0">
              <span class="mr-2">
                <eg-b-icon icon="arrow-left" />
              </span>
              Go back
            </div>
          </eg-b-button>

          <div>
            <eg-b-button
              v-if="
                quizeSteps[currentStepIndex].canSkip &&
                currentStepIndex !== steps.length - 1
              "
              class="ml-3 action-button"
              variant="light"
              :disabled="slotLoading"
              @click="skip(goNext, currentStepIndex)"
            >
              Skip
            </eg-b-button>

            <eg-b-button
              class="ml-3 action-button"
              variant="info"
              :disabled="slotLoading || !quizeSteps[currentStepIndex].answer"
              @click="goNext()"
            >
              <eg-b-spinner v-if="slotLoading" small></eg-b-spinner>
              {{ currentStepIndex === steps.length - 1 ? "Finalize" : "Next" }}
            </eg-b-button>
          </div>
        </div>
      </template>
    </eg-stepper>
  </div>
</template>

<script>
// import { sendQuiz } from "@/api";
import EgStepper from "@/components/global/eg_stepper.vue";
import QuizeStep from "@/components/dashboard/quiz/first_quiz_step.vue";
import WhereDoYouTeachStep from "@/components/dashboard/quiz/where_do_you_teach_step.vue";
import gTagMixin from "@/components/auth/gTagMixin";
import { USER_ROLE } from "@/constants";
// import { mergeUserData } from "@/utils/userUtils";

/** Role id strings (match USER_ROLE keys) — API expects /users/me `role` as a string, not a label object */
const USER_ROLE_QUIZ_VALUE_KEYS = [
  "teacherEducator",
  "principalVicePrincipal",
  "schoolAdministrator",
  "districtAdministrator",
  "iTTechnologyDirector",
  "edTechCoordinator",
  "curriculumCoordinator",
  "student",
  "other",
];

const USER_ROLE_QUIZ_OPTIONS = USER_ROLE_QUIZ_VALUE_KEYS.map((key) => ({
  translationKey: USER_ROLE[key].translationKey,
  value: key,
}));

export default {
  name: "FirstQuiz",

  components: {
    EgStepper,
    QuizeStep,
    WhereDoYouTeachStep,
  },

  mixins: [gTagMixin],

  data() {
    return {
      activeStepIndex: 0,
      loading: false,
      conversionTracked: false, // Flag to track signup conversion only once
      quizeSteps: [
        {
          title: "What's your current role?",
          key: "are_you_a",
          answer: "",
          otherAnswer: "",
          options: USER_ROLE_QUIZ_OPTIONS,
          placeholder: "Please enter your role",
        },
        {
          title: "Almost done - how did you find us?",
          key: "how_did_you_find_us",
          answer: "",
          otherAnswer: "",
          placeholder: "Please let us know how you found us",
          options: [
            {
              // icon: "google",
              label: "Google Search",
              value: "Google Search",
            },
            {
              // icon: "people-fill",
              label: "Bing Search",
              value: "Bing Search",
            },
            {
              // icon: "calendar2-month-fill",
              label: "LLM (like ChatGPT, Claude, etc)",
              value: "LLM (like ChatGPT, Claude, etc)",
            },
            {
              // icon: "mortarboard-fill",
              label: "Recommended by a teacher colleague",
              value: "Recommended by a teacher colleague",
            },
            {
              // icon: "chat-dots-fill",
              label: "Social Media",
              value: "social_media",
            },
            {
              // icon: "other",
              label: "Other",
              value: "other",
            },
          ],
        },
        // {
        //   title: "Hey! Are you a...",
        //   answer: "",
        //   otherAnswer: "",
        //   options: [
        //     {
        //       icon: "emoji-smile-fill",
        //       label: "Teacher",
        //       value: "teacher",
        //     },
        //     {
        //       icon: "buildings-fill",
        //       label: "District",
        //       value: "district",
        //     },
        //     {
        //       icon: "mortarboard-fill",
        //       label: "Principal",
        //       value: "principal",
        //     },
        //     {
        //       icon: "building-fill",
        //       label: "Admin",
        //       value: "admin",
        //     },
        //     {
        //       icon: "clipboard-check-fill",
        //       label: "Dept. Head",
        //       value: "department_head",
        //     },
        //     {
        //       icon: "other",
        //       label: "Other",
        //       value: "other",
        //     },
        //   ],
        // },
        // {
        //   title: "Where do you teach?",
        //   answer: null,
        //   options: [],
        //   component: "where-do-you-teach-step",
        // },
        // {
        //   title: "What grade level do you teach?",
        //   isMultiselect: true,
        //   answer: "",
        //   otherAnswer: "",
        //   options: [
        //     {
        //       icon: "puzzle-fill",
        //       label: "Elementary",
        //       value: "elementary",
        //     },
        //     {
        //       icon: "backpack-fill",
        //       label: "Middle School",
        //       value: "middle_school",
        //     },
        //     {
        //       icon: "mortarboard-fill",
        //       label: "High School",
        //       value: "high_school",
        //     },
        //     {
        //       icon: "bank",
        //       label: "College",
        //       value: "college",
        //     },
        //     {
        //       icon: "other",
        //       label: "Other",
        //       value: "other",
        //     },
        //   ],
        // },
        // {
        //   title: "What subjects are you interested in?",
        //   isMultiselect: true,
        //   answer: "",
        //   otherAnswer: "",
        //   options: [
        //     {
        //       icon: "book-half",
        //       label: "English",
        //       value: "english",
        //     },
        //     {
        //       icon: "bank",
        //       label: "History",
        //       value: "history",
        //     },
        //     {
        //       icon: "flask",
        //       label: "Science",
        //       value: "science",
        //     },
        //     {
        //       icon: "math",
        //       label: "Math",
        //       value: "math",
        //     },
        //     {
        //       icon: "globe-europe-africa",
        //       label: "Languages",
        //       value: "languages",
        //     },
        //     {
        //       icon: "palette-fill",
        //       label: "Arts",
        //       value: "arts",
        //     },
        //     {
        //       icon: "other",
        //       label: "Other",
        //       value: "other",
        //     },
        //   ],
        // },
        // {
        //   title: "How did you find us?",
        //   answer: "",
        //   otherAnswer: "",
        //   options: [
        //     {
        //       icon: "google",
        //       label: "Google Search",
        //       value: "google_search",
        //     },
        //     {
        //       icon: "people-fill",
        //       label: "Friend/Colleague",
        //       value: "friend_colleague",
        //     },
        //     {
        //       icon: "chat-dots-fill",
        //       label: "Social Media",
        //       value: "social_media",
        //     },
        //     {
        //       icon: "calendar2-month-fill",
        //       label: "Conference/Event",
        //       value: "conference_event",
        //     },
        //     {
        //       icon: "mortarboard-fill",
        //       label: "School/District",
        //       value: "school_district",
        //     },
        //     {
        //       icon: "envelope-paper-fill",
        //       label: "Email",
        //       value: "email",
        //     },
        //     {
        //       icon: "other",
        //       label: "Other",
        //       value: "other",
        //     },
        //   ],
        // },
      ],
      isSkipping: false,
    };
  },

  methods: {
    handleInputOther(index, value) {
      this.quizeSteps[index].otherAnswer = value;
    },

    beforeChange(step) {
      // Track signup conversion only once on first "Next" click
      if (!this.conversionTracked && this.activeStepIndex === 0) {
        this.gtag_report_conversion();
        this.bing_report_conversion();
        this.conversionTracked = true;
      }

      if (this.isSkipping) {
        step.resolve();

        return;
      }

      if (this.quizeSteps[this.activeStepIndex].answer) {
        step.resolve();

        return;
      }

      step.reject();
    },

    async finishHandler(resolve) {
      if (!this.$listeners.finish) {
        console.error("finish listener is not defined");
        resolve();

        return;
      }

      this.$emit("finish", {
        answers: this.quizeSteps.reduce((acc, step) => {
          const isOther =
            step.answer === "other" ||
            (step.answer &&
              typeof step.answer === "object" &&
              step.answer.translationKey === USER_ROLE.other.translationKey);
          acc[step.key] = isOther ? step.otherAnswer : step.answer;
          return acc;
        }, {}),
        resolve,
      });
    },

    async skip(goNext, currentStepIndex) {
      this.quizeSteps[currentStepIndex].answer = "";

      this.isSkipping = true;
      await goNext();
      this.isSkipping = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.title {
  font-family: var(--typography-font-family-text-primary);
  font-size: var(--typography-font-size-fs-600);
  font-weight: var(--typography-font-weight-fw-600);
  line-height: 120%;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 24px;
    line-height: 32px;
  }
}

.first-quiz-container {
  background-color: #fff;
  width: 100%;
  min-height: 422px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  & h5 {
    font-family: var(--typography-font-family-text-primary);
    font-size: var(--typography-font-size-fs-500);
    font-weight: var(--typography-font-weight-fw-600);
    line-height: 120%;
    text-align: center;
  }
}

.stepper-actions .action-button {
  width: 170px;
}

@media (max-width: 768px) {
  .stepper-actions {
    justify-content: center;

    .text-info {
      display: none;
    }
  }
}
</style>
